import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
import { StatePrivacyRights } from '../components/StatePrivacyRights';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Learn how FindOnlineDegrees collects, uses, protects, and shares personal information when you use our website and services.'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-2121" data-mp="2e20fm1v3vf2n">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-2122" data-mp="2dzw34vj557qr">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2123" data-mp="2iu0c1kbyvpuh">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-2124" data-mp="2442u3m4mrwdq">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-2125" data-mp="291umeh96tkig">Last updated: September 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-2126" data-mp="1v3ee4lvofly7">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-2127" data-mp="1z4zs6ajy669z">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2128" data-mp="29rmvzoiz206v">
            <p data-id="element-2129" data-mp="29ps9i5s1y00o">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-2130" data-mp="2eq6ipr5e3g0a">
              <li data-id="element-2131" data-mp="2eomyjpgm4kt8">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-2132" data-mp="1m2kb87t1jr85">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-2133" data-mp="20srp0a83sm2i">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-2134" data-mp="1synb9xa9xxd9">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2135" data-mp="1sz6qh68ji7nl">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2136" data-mp="1wz73qnjwkct2">
              <ul className="list-disc pl-6 space-y-2" data-id="element-2137" data-mp="21tbfrl80zb10">
                <li data-id="element-2138" data-mp="26qtobai4ro8h">
                  <strong data-id="element-2139" data-mp="26q1rxxgplmh5">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-2140" data-mp="1izxopr7fnoqo">
                  <strong data-id="element-2141" data-mp="1j3ljetom6h6z">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-2142" data-mp="2ufw40ct7f70c">
                  <strong data-id="element-2143" data-mp="2uis1y151xvos">Do we receive information from third parties?</strong>{' '}
                  We may receive technical and advertising data from third
                  parties, including advertising platforms and analytics
                  providers.
                </li>
                <li data-id="element-2144" data-mp="ituag9elez99">
                  <strong data-id="element-2145" data-mp="ivf1vq5m222w">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-2146" data-mp="4zkvbdsb7osy">
                  <strong data-id="element-2147" data-mp="348cmwujghg">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-2148" data-mp="3mc5bef0eix9c">
                  <strong data-id="element-2149" data-mp="1phiqgodd053z">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-2150" data-mp="1sjn8uu2s259b">
                  <strong data-id="element-2151" data-mp="1sj41266ucx90">What are your rights?</strong> You may access,
                  correct, delete, and port your personal data, opt out of its
                  sale, sharing, and use for targeted advertising, and appeal
                  our decisions.
                </li>
                <li data-id="element-2152" data-mp="1njreznnsnu7h">
                  <strong data-id="element-2153" data-mp="1nj9a62n2g7ny">How do you exercise your rights?</strong> Use the
                  request options in{' '}
                  <a href="#exercise-your-rights" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-2154" data-mp="19rb7oanv1a0r">
                    Section 9
                  </a>
                  , email us, or write to us (see Section 12). We honor
                  state privacy rights for all U.S. residents.
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-2155" data-mp="qujqc6rvp4zc">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2156" data-mp="qw4m1zpxitys">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-2157" data-mp="c7bb8uvg8fi3">
              <li data-id="element-2158" data-mp="c7l3yq7vp0rh">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-2159" data-mp="78rquywi7bru">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-2160" data-mp="7ab7iqm2nou5">SMS / TEXT MESSAGE COMMUNICATIONS</li>
              <li data-id="element-2161" data-mp="l92e7rylu889">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-2162" data-mp="g8epjcxum3h7">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-2163" data-mp="gb97mxte3c7g">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-2164" data-mp="v4m9qd99b4wh">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-2165" data-mp="q7m3o2ceq25d">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-2166" data-mp="q4zxo755zup9">WHAT ARE YOUR PRIVACY RIGHTS? (INCLUDING STATE PRIVACY RIGHTS)</li>
              <li data-id="element-2167" data-mp="13xz7bsi9muow">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-2168" data-mp="zwdsw2q328cl">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-2169" data-mp="zz088oj7ts5d">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-2170" data-mp="18yd9wv13lcfj">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2171" data-mp="18ywqo8f0hurz">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2172" data-mp="6i304lp4ilfp">
              <h3 className="font-bold text-lg" data-id="element-2173" data-mp="amkdo34w42i1">
                Personal information you disclose to us
              </h3>
              <p data-id="element-2174" data-mp="firdrfalujs2">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-2175" data-mp="yblkjly2r5l7">
                <strong data-id="element-2176" data-mp="yb3lkhk12d9l">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2177" data-mp="1382hn5n94uaf">
                <li data-id="element-2178" data-mp="303rsp1zslkun">Names</li>
                <li data-id="element-2179" data-mp="304t79gg0oph4">Phone numbers</li>
                <li data-id="element-2180" data-mp="351jpoafanx5p">Email addresses</li>
                <li data-id="element-2181" data-mp="352bixelf5ist">Mailing addresses</li>
                <li data-id="element-2182" data-mp="2xiab82rxl89s">Education level and interests</li>
                <li data-id="element-2183" data-mp="2xj261bdpf1z0">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-2184" data-mp="2sr23db691uyq">
                Information automatically collected
              </h3>
              <p data-id="element-2185" data-mp="2ntime3i63433">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2186" data-mp="3bjd1ystvf6r1">
                <li data-id="element-2187" data-mp="36ioznb06z3hi">IP address</li>
                <li data-id="element-2188" data-mp="36hxhp2zip49p">Browser and device characteristics</li>
                <li data-id="element-2189" data-mp="32hdi7srh1oke">Operating system</li>
                <li data-id="element-2190" data-mp="32gbxsxokfpq0">Language preferences</li>
                <li data-id="element-2191" data-mp="32jpwnvy0tgsp">Referring URLs</li>
                <li data-id="element-2192" data-mp="1v41z1k9emddq">Device name and type</li>
                <li data-id="element-2193" data-mp="1v1p8wjthswpl">Pages viewed and time spent</li>
                <li data-id="element-2194" data-mp="1r1ym0r7370qc">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-2195" data-mp="3q92qh67a49uw">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2196" data-mp="3v61jtmj4ccs1">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2197" data-mp="3h71ro4wedfr4">
              <p data-id="element-2198" data-mp="3m53duyp4dv6t">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2199" data-mp="dqupy3xjlskz">
                <li data-id="element-2200" data-mp="hqwvwpab9862">To facilitate account creation and authentication</li>
                <li data-id="element-2201" data-mp="ht97mkph6mao">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-2202" data-mp="3z8huggi49mg">To respond to user inquiries and offer support</li>
                <li data-id="element-2203" data-mp="8y1pvtd0r8f8">To send administrative information to you</li>
                <li data-id="element-2204" data-mp="wofjhob95fbx">To fulfill and manage your requests</li>
                <li data-id="element-2205" data-mp="wmuoxyoxtwe2">To enable user-to-user communications</li>
                <li data-id="element-2206" data-mp="11fnnu5xq6ai8">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-2207" data-mp="1as4r5h07ok8n">To send you marketing and promotional communications</li>
                <li data-id="element-2208" data-mp="16rkro6s614jc">To deliver targeted advertising to you</li>
                <li data-id="element-2209" data-mp="1kjhh232g90c1">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-2210" data-mp="1fkedkn24b266">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-2211" data-mp="rx6927cijx57">To save or protect an individual's vital interest</li>
                <li data-id="element-2212" data-mp="nv2s5372s0cj">To comply with our legal obligations</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-2213" data-mp="11uata1mstijd">Email Communications</h3>
              <p data-id="element-2214" data-mp="wtn6xt9ilpvh">
                When you submit a form on our website, you may consent to
                receive marketing and promotional emails from
                FindOnlineDegrees.org (operated by Media-Matchers LLC) and its
                educational partners at the email address you provide. Marketing
                emails are sent from contact@findonlinedegrees.org. Every
                marketing email includes an unsubscribe link. You may opt out at
                any time and your request will be honored within 10 business
                days. Opting out of marketing email does not affect
                transactional or administrative messages related to a specific
                inquiry you have submitted.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2215" data-mp="xqukeak2fusp">
                Advertising Pixels and Third-Party Measurement
              </h3>
              <p data-id="element-2216" data-mp="nuhqbbklzexf">
                We use pixels, web beacons, cookies, and other storage
                technologies from third parties, including Meta (Facebook), to
                collect or receive information from our websites and elsewhere
                on the internet. Those third parties, including Meta, use that
                information to provide measurement services, and to target and
                deliver advertising. This may include sharing a hashed
                identifier and information about the actions you take on our
                site — such as viewing a page or submitting a request for
                information — so that advertising can be measured and
                attributed. We do not share the substance of any sensitive
                information for advertising purposes.
              </p>
              <p data-id="element-2217" data-mp="22teyvkxdqp3k">
                You can opt out of the collection and use of this information
                for ad targeting at{' '}
                <a href="http://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] underline hover:no-underline" data-id="element-2218" data-mp="2bsijwug3xekp">
                  aboutads.info/choices
                </a>{' '}
                (US) or{' '}
                <a href="http://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] underline hover:no-underline" data-id="element-2219" data-mp="3s5pixl2od8xq">
                  youronlinechoices.eu
                </a>{' '}
                (EU), and manage Meta ad preferences at{' '}
                <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] underline hover:no-underline" data-id="element-2220" data-mp="37hzgrf4tj01x">
                  facebook.com/settings?tab=ads
                </a>
                . Our{' '}
                <a href="/cookie-policy" className="text-[#3b82f6] underline hover:no-underline" data-id="element-2221" data-mp="2tjhy3vtrsdqv">
                  Cookie Policy
                </a>{' '}
                explains these technologies in more detail.
              </p>
            </div>
          </div>

          {/* 3. SMS / Text Message Communications */}
          <div data-id="element-2222" data-mp="bjh1a2uhiux0">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2223" data-mp="bixiyl0xa4ig">
              3. SMS / TEXT MESSAGE COMMUNICATIONS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2224" data-mp="gd1ttfdb6wot">
              <h3 className="font-bold text-lg" data-id="element-2225" data-mp="1nx5dgfx7w89">How We Collect SMS Consent</h3>
              <p data-id="element-2226" data-mp="6mgs3j2c9c1o">
                When you submit a form on our website, you may expressly consent
                to receive autodialed and/or pre-recorded marketing text
                messages (SMS/MMS) from FindOnlineDegrees.org (operated by Media
                Matchers LLC) and its educational partners at the mobile phone
                number you provide. This consent is obtained at the point of
                form submission and is documented with the date, time, IP
                address, and page URL at which consent was given.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2227" data-mp="3a1vzecjc17j4">
                How We Use Your Phone Number for SMS
              </h3>
              <p data-id="element-2228" data-mp="3qabymi4o5q3i">
                If you have provided consent, we may send you text messages for
                purposes including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2229" data-mp="3lgr26uiq546f">
                <li data-id="element-2230" data-mp="3gk8ylxz4rbvo">
                  Information about educational programs matching your interests
                </li>
                <li data-id="element-2231" data-mp="3bib6sfcnz8r4">Follow-up communications related to your inquiry</li>
                <li data-id="element-2232" data-mp="d1foxw9h376s">
                  Promotional offers and marketing messages from us or our
                  educational partners
                </li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-2233" data-mp="33t5qxl54th0">
                Consent Is Not Required
              </h3>
              <p data-id="element-2234" data-mp="3v7bw44ngdkj0">
                Your consent to receive SMS/MMS messages is never a condition of
                using our services or receiving information. You may choose not
                to provide consent and still access our website and resources.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2235" data-mp="zbpfqk0pea44">
                Message Frequency and Rates
              </h3>
              <p data-id="element-2236" data-mp="13dstxgerbst3">
                Message frequency varies based on your interactions and
                preferences. Standard message and data rates may apply depending
                on your carrier and plan.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2237" data-mp="1d3vjt7ql0qn7">How to Opt Out</h3>
              <p data-id="element-2238" data-mp="1i0m1fzi1buaq">
                You may revoke your consent and opt out of SMS communications at
                any time by:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2239" data-mp="1n2l3n621gl63">
                <li data-id="element-2240" data-mp="1r3n57tjfh9b8">Replying STOP to any text message you receive from us</li>
                <li data-id="element-2241" data-mp="1w1onwdcxz6m6">
                  Contacting us directly using the information in Section 12
                </li>
              </ul>
              <p data-id="element-2242" data-mp="3sv9r5gogqe0u">
                Opt-out requests will be honored promptly. After opting out, you
                may receive a single confirmation message. You will not receive
                further SMS marketing messages unless you provide new consent.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2243" data-mp="1009s1lck6j1q">For Assistance</h3>
              <p data-id="element-2244" data-mp="1dyr9j1bvezb7">
                Reply HELP to any text message for support information, or
                contact us directly using the information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2245" data-mp="1nx72l9ugkinc">
                Third-Party SMS Senders
              </h3>
              <p data-id="element-2246" data-mp="1iycebsdq21vu">
                When you consent to receive communications from our educational
                partners, those partners may contact you directly via SMS under
                their own messaging programs and privacy policies. We encourage
                you to review the privacy policies of any educational
                institution or partner that contacts you.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-2247" data-mp="21p3rz64qseg1">Record Retention</h3>
              <p data-id="element-2248" data-mp="2lads38i5rdbe">
                We retain records of SMS consent, including timestamp, IP
                address, form URL, and submitted form data, in accordance with
                our general data retention practices described in Section 6.
              </p>
            </div>
          </div>

          {/* 4. When and With Whom Do We Share */}
          <div data-id="element-2249" data-mp="2g9zhbip69p9o">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2250" data-mp="2g9fz00vm0yv4">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2251" data-mp="27aukqhx68605">
              <p data-id="element-2252" data-mp="1slq2j0qdm1s1">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-2253" data-mp="1snj6u9dio9wv">
                <li data-id="element-2254" data-mp="1xjzzngvnvcga">
                  <strong data-id="element-2255" data-mp="1xlkpit71677t">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-2256" data-mp="1nopuwldvoqyt">
                  <strong data-id="element-2257" data-mp="2424dsn56n56k">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-2258" data-mp="1u3on2la2hxxl">
                  <strong data-id="element-2259" data-mp="1u35ehv6b4lw8">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-2260" data-mp="1l4bnckbqwnq2">
                  <strong data-id="element-2261" data-mp="1l4tex3ngqdrn">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-2262" data-mp="1bd795mtw96wa">
                  <strong data-id="element-2263" data-mp="1bcnr8648uii9">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 5. Cookies and Tracking */}
          <div data-id="element-2264" data-mp="11hdm39ijtv09">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2265" data-mp="xdo0msx67tjg">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2266" data-mp="3909fmn0n57tb">
              <p data-id="element-2267" data-mp="3d23a2dk8tfl1">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 6. How Long Do We Keep */}
          <div data-id="element-2268" data-mp="2pdtn38bamlsb">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2269" data-mp="2ped0qctwuo0j">
              6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2270" data-mp="2u9scmz8cy9ub">
              <p data-id="element-2271" data-mp="2z9n2r0kb6veb">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 7. How Do We Keep Information Safe */}
          <div data-id="element-2272" data-mp="1ohhva9smk5ef">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2273" data-mp="1oh9dq4lk4bww">
              7. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2274" data-mp="1tfjd6rebdyob">
              <p data-id="element-2275" data-mp="1yfypdfmv104p">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 8. Minors */}
          <div data-id="element-2276" data-mp="i2gtvoktyv0g">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2277" data-mp="i30drau1jth4">
              8. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2278" data-mp="mzh5sg4d2rzh">
              <p data-id="element-2279" data-mp="30ajsqgjebkdy">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 9. Privacy Rights */}
          <div data-id="element-2280" data-mp="2clh4uqy64vb0">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2281" data-mp="2clqziru5rqn1">
              9. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            {/* Anchor target for footer "Your Privacy Choices" style links. */}
            <span id="your-privacy-choices" className="block -mt-4" aria-hidden="true" data-id="element-2282" data-mp="23mlxvcnavh6n" />
            <StatePrivacyRights data-id="element-2283" data-mp="1yl6c1idat6n7" />
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2284" data-mp="1ylg2f7299ftf">
              <h3 className="font-bold text-lg mt-6" data-id="element-2285" data-mp="1to69vijkko1d">
                Opt Out of Marketing Email
              </h3>
              <p data-id="element-2286" data-mp="1jxxyiawiop82">
                To opt out of marketing email: Click the unsubscribe link in any
                marketing email you receive from us, or contact us at the
                address in Section 12. Requests are processed within 10 business
                days. Opting out of marketing email does not by itself opt you
                out of the sale or sharing of personal data — use the request
                options above for that.
              </p>
            </div>
          </div>

          {/* 10. Do Not Track */}
          <div data-id="element-2287" data-mp="1txoqa4tp0q4g">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2288" data-mp="1ty8a5r2wlol4">
              10. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2289" data-mp="1xxqv2muq5zny">
              <p data-id="element-2290" data-mp="2lomht5kbq4oc">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. No uniform standard for
                recognizing DNT signals has been finalized, so we do not respond
                to DNT signals. We do honor the Global Privacy Control (GPC)
                signal as an opt-out of sale, sharing, and targeted advertising,
                as described in Section 9.
              </p>
            </div>
          </div>

          {/* 11. Updates */}
          <div data-id="element-2291" data-mp="3s873vij01v1o">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2292" data-mp="3s7xg81lffzz5">
              11. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2293" data-mp="3nafaek2pi0vd">
              <p data-id="element-2294" data-mp="618vinrjkvku">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 12. Contact Us */}
          <div data-id="element-2295" data-mp="3i4jx14oo0vpi">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-2296" data-mp="3i4tsh7shbv2l">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-2297" data-mp="3n3n2urcgz5yj">
              <p data-id="element-2298" data-mp="3r3x6vygpbmar">
                If you have questions or comments about this notice, please
                contact us at:
              </p>
              <p className="font-semibold" data-id="element-2299" data-mp="3w1ozyvt31egj">Media-Matchers LLC</p>
              <p data-id="element-2300" data-mp="4wz0h98t0wwk">
                1865 Chestnut Street
                <br data-id="element-2301" data-mp="9wlevm0dcj6y" />
                San Francisco, CA 94123
              </p>
              <p data-id="element-2302" data-mp="9uieq95i59oi">
                Email:{' '}
                <a href="mailto:contact@findonlinedegrees.org" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-2303" data-mp="eomkip317bo0">
                  contact@findonlinedegrees.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}